import { useMemo, useState } from "react";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CheckCircle2,
  Circle,
  Clock3,
  Inbox,
  Lightbulb,
  ListTodo,
  Mic,
  Plus,
  RotateCcw,
  Scale,
  Sparkles
} from "lucide-react";
import clsx from "clsx";
import type { DailyFocus, Decision, Task, Thought } from "../types";
import {
  createTodayFocus,
  saveRecord
} from "../lib/db";
import {
  addLocalDays,
  formatChineseDate,
  formatShortDate,
  isFollowUpDue,
  isReviewDue,
  isTaskDue,
  localDateKey,
  nowIso
} from "../lib/time";
import { Button, EmptyState, Modal, PageHeader, StatusPill } from "../components/ui";
import { TaskEditor } from "../components/TaskEditor";

export function TodayView({
  tasks,
  thoughts,
  decisions,
  focuses,
  onOpenCapture,
  onOpenVoice,
  onNavigate,
  notify
}: {
  tasks: Task[];
  thoughts: Thought[];
  decisions: Decision[];
  focuses: DailyFocus[];
  onOpenCapture: (type?: "thought" | "task" | "decision") => void;
  onOpenVoice: () => void;
  onNavigate: (view: "tasks" | "thinking" | "review") => void;
  notify: (message: string, tone?: "default" | "error") => void;
}) {
  const today = localDateKey();
  const dateLabel = formatChineseDate();
  const focus = focuses.find((item) => item.date === today);
  const focusIds = focus?.taskIds ?? [];
  const focusTasks = focusIds
    .map((id) => tasks.find((task) => task.id === id))
    .filter((task): task is Task => Boolean(task));
  const inbox = tasks.filter((task) => task.status === "inbox");
  const waiting = tasks.filter((task) => task.status === "waiting");
  const dueWaiting = waiting.filter((task) => isFollowUpDue(task.followUpAt));
  const dueDecisions = decisions.filter(
    (decision) => decision.status === "active" && isReviewDue(decision.reviewAt)
  );
  const openThoughts = thoughts.filter((thought) => thought.status === "open");
  const overdue = tasks.filter(
    (task) =>
      task.status !== "done" &&
      task.status !== "dropped" &&
      isTaskDue(task.dueAt)
  );

  const [pickerOpen, setPickerOpen] = useState(false);
  const [editorTask, setEditorTask] = useState<Task | undefined>();
  const [commitAfterSave, setCommitAfterSave] = useState(false);
  const [closeoutOpen, setCloseoutOpen] = useState(false);
  const [closeNote, setCloseNote] = useState(focus?.closeNote ?? "");

  const activeTasks = useMemo(
    () =>
      tasks
        .filter((task) => task.status !== "done" && task.status !== "dropped")
        .sort((a, b) => {
          const aDate = a.dueAt ?? "9999";
          const bDate = b.dueAt ?? "9999";
          return aDate.localeCompare(bDate) || a.createdAt.localeCompare(b.createdAt);
        }),
    [tasks]
  );

  async function addToFocus(task: Task) {
    const currentFocus = focus ?? (await createTodayFocus(today));
    if (currentFocus.taskIds.includes(task.id)) {
      notify("这件事已经在今日聚焦里");
      return;
    }
    if (currentFocus.taskIds.length >= 3) {
      notify("今日最多聚焦三件事，先移出一件再添加。", "error");
      return;
    }
    let nextTask = task;
    if (task.status === "inbox") {
      nextTask = await saveRecord({ ...task, status: "next" });
    }
    const updated = {
      ...currentFocus,
      taskIds: [...currentFocus.taskIds, nextTask.id],
      closedAt: undefined
    };
    await saveRecord(updated);
    setPickerOpen(false);
    notify("已放入今日聚焦");
  }

  function selectTask(task: Task) {
    if (!task.outcome.trim() || !task.nextAction.trim()) {
      setEditorTask(task);
      setCommitAfterSave(true);
      setPickerOpen(false);
      return;
    }
    void addToFocus(task);
  }

  async function removeFromFocus(taskId: string) {
    if (!focus) return;
    await saveRecord({ ...focus, taskIds: focus.taskIds.filter((id) => id !== taskId) });
    notify("已移出今日聚焦");
  }

  async function markDone(task: Task) {
    const timestamp = nowIso();
    await saveRecord({ ...task, status: "done", completedAt: timestamp });
    notify("已完成");
  }

  async function moveToTomorrow(task: Task) {
    const currentFocus = focus;
    await saveRecord({
      ...task,
      status: task.status === "inbox" ? "next" : task.status,
      dueAt: addLocalDays(today, 1)
    });
    if (currentFocus) {
      await saveRecord({
        ...currentFocus,
        taskIds: currentFocus.taskIds.filter((id) => id !== task.id)
      });
    }
    notify("已移到明天");
  }

  async function saveCloseout() {
    const currentFocus = focus ?? (await createTodayFocus(today));
    await saveRecord({
      ...currentFocus,
      closeNote: closeNote.trim(),
      closedAt: currentFocus.closedAt ?? nowIso()
    });
    setCloseoutOpen(false);
    notify("今日收尾已记录");
  }

  return (
    <div className="view today-view">
      <PageHeader
        eyebrow={dateLabel}
        title="今天，把力气用在最关键的地方"
        description="不用处理所有事。先确定三件真正要推动的结果。"
        action={
          <div className="header-actions">
            <Button type="button" onClick={onOpenVoice}>
              <Mic size={17} /> 语音整理
            </Button>
            <Button type="button" onClick={() => onOpenCapture("thought")}>
              <Lightbulb size={17} /> 记想法
            </Button>
            <Button type="button" variant="primary" onClick={() => setPickerOpen(true)}>
              <Plus size={17} /> 加入今日
            </Button>
          </div>
        }
      />

      <section className="today-hero">
        <div className="today-hero-top">
          <div>
            <p className="eyebrow">今日聚焦</p>
            <h2>{focusTasks.length === 0 ? "还没有选定今天的主战场" : `${focusTasks.length} 件关键事`}</h2>
          </div>
          <div className="focus-counter" aria-label={`今日已选 ${focusTasks.length} 件，共 3 件`}>
            <strong>{focusTasks.length}</strong>
            <span>/ 3</span>
          </div>
        </div>

        {focusTasks.length === 0 ? (
          <div className="focus-empty">
            <div>
              <Circle size={22} />
              <p>从待办里挑最多三件，今天只认结果。</p>
            </div>
            <Button type="button" variant="secondary" onClick={() => setPickerOpen(true)}>
              选择今日三件事 <ArrowRight size={16} />
            </Button>
          </div>
        ) : (
          <div className="focus-list">
            {focusTasks.map((task, index) => (
              <article className="focus-item" key={task.id}>
                <div className="focus-index">{String(index + 1).padStart(2, "0")}</div>
                <div className="focus-content">
                  <div className="focus-title-row">
                    <button
                      type="button"
                      className="text-button title-button"
                      onClick={() => {
                        setEditorTask(task);
                        setCommitAfterSave(false);
                      }}
                    >
                      {task.title}
                    </button>
                    <StatusPill status={task.status} compact />
                  </div>
                  <p className="focus-outcome">{task.outcome}</p>
                  <div className="focus-next">
                    <span>下一步</span>
                    <p>{task.nextAction}</p>
                  </div>
                  <div className="focus-meta">
                    <span>{task.area || "未分类"}</span>
                    {task.dueAt && (
                      <span className={clsx(isTaskDue(task.dueAt) && "danger-text")}>
                        <CalendarDays size={14} /> {formatShortDate(task.dueAt)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="focus-actions">
                  <button type="button" className="check-button" onClick={() => void markDone(task)} aria-label={`完成 ${task.title}`}>
                    <Check size={19} />
                  </button>
                  <button type="button" className="quiet-button" onClick={() => void removeFromFocus(task.id)}>
                    移出
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="today-hero-footer">
          <button type="button" className="text-button" onClick={() => setCloseoutOpen(true)}>
            <Clock3 size={16} /> 两分钟收尾
          </button>
          <button type="button" className="text-button" onClick={() => onOpenCapture("task")}>
            <Plus size={16} /> 记一个待办
          </button>
          <button type="button" className="text-button" onClick={onOpenVoice}>
            <Mic size={16} /> 录一段再说
          </button>
        </div>
      </section>

      <div className="today-grid">
        <section className="surface">
          <div className="section-heading">
            <div>
              <p className="eyebrow">需要留意</p>
              <h2>等待与承诺</h2>
            </div>
            <button type="button" className="text-button" onClick={() => onNavigate("tasks")}>
              全部任务 <ArrowRight size={15} />
            </button>
          </div>
          {dueWaiting.length === 0 && overdue.length === 0 ? (
            <div className="quiet-state">
              <CheckCircle2 size={22} />
              <p>今天没有逾期或到点跟进的等待事项。</p>
            </div>
          ) : (
            <div className="compact-list">
              {overdue.slice(0, 3).map((task) => (
                <button key={task.id} type="button" className="compact-row" onClick={() => setEditorTask(task)}>
                  <span className="compact-icon danger">
                    <Clock3 size={17} />
                  </span>
                  <span>
                    <strong>{task.title}</strong>
                    <small>已逾期 · {task.area || "未分类"}</small>
                  </span>
                  <span className="danger-text">{formatShortDate(task.dueAt)}</span>
                </button>
              ))}
              {dueWaiting.slice(0, 3).map((task) => (
                <button key={task.id} type="button" className="compact-row" onClick={() => setEditorTask(task)}>
                  <span className="compact-icon amber">
                    <Clock3 size={17} />
                  </span>
                  <span>
                    <strong>{task.title}</strong>
                    <small>等待 {task.waitingOn || "他人"}</small>
                  </span>
                  <span>{formatShortDate(task.followUpAt)}</span>
                </button>
              ))}
            </div>
          )}
        </section>

        <section className="surface">
          <div className="section-heading">
            <div>
              <p className="eyebrow">还没归位</p>
              <h2>收件箱与思考</h2>
            </div>
            <button type="button" className="text-button" onClick={() => onNavigate("tasks")}>
              去整理 <ArrowRight size={15} />
            </button>
          </div>
          <div className="two-stat-row">
            <button type="button" className="mini-stat" onClick={() => onNavigate("tasks")}>
              <span className="mini-stat-icon"><Inbox size={19} /></span>
              <span><strong>{inbox.length}</strong><small>待办待归位</small></span>
            </button>
            <button type="button" className="mini-stat" onClick={() => onNavigate("thinking")}>
              <span className="mini-stat-icon"><Sparkles size={19} /></span>
              <span><strong>{openThoughts.length}</strong><small>思考待推进</small></span>
            </button>
          </div>
          <div className="attention-lines">
            <button type="button" onClick={() => onNavigate("thinking")}>
              <Scale size={17} />
              <span>{dueDecisions.length > 0 ? `${dueDecisions.length} 个决策到了复核日` : "所有决策复核均在计划内"}</span>
              <ArrowRight size={15} />
            </button>
            <button type="button" onClick={() => onOpenCapture("thought")}>
              <Lightbulb size={17} />
              <span>有新的判断，先记下来</span>
              <ArrowRight size={15} />
            </button>
          </div>
        </section>
      </div>

      <section className="weekly-cue">
        <div>
          <p className="eyebrow">每周十五分钟</p>
          <h2>把做过的事，变成下一次判断的依据</h2>
        </div>
        <Button type="button" onClick={() => onNavigate("review")}>
          开始周复盘 <ArrowRight size={16} />
        </Button>
      </section>

      {pickerOpen && (
        <TaskPicker
          tasks={activeTasks}
          selectedIds={focusIds}
          onSelect={selectTask}
          onClose={() => setPickerOpen(false)}
          onCreate={() => {
            setPickerOpen(false);
            setEditorTask(undefined);
            setCommitAfterSave(true);
          }}
        />
      )}

      {editorTask && (
        <TaskEditor
          task={editorTask}
          onClose={() => {
            setEditorTask(undefined);
            setCommitAfterSave(false);
          }}
          onSaved={(saved) => {
            if (commitAfterSave) {
              void addToFocus(saved);
            } else {
              notify("待办已更新");
            }
            setEditorTask(undefined);
            setCommitAfterSave(false);
          }}
        />
      )}

      {commitAfterSave && !editorTask && (
        <TaskEditor
          onClose={() => setCommitAfterSave(false)}
          onSaved={(saved) => {
            setCommitAfterSave(false);
            void addToFocus(saved);
          }}
        />
      )}

      {closeoutOpen && (
        <Modal
          title="今天到这里"
          eyebrow="两分钟收尾"
          onClose={() => setCloseoutOpen(false)}
          size="medium"
          footer={
            <>
              <Button type="button" onClick={() => setCloseoutOpen(false)}>取消</Button>
              <Button type="button" variant="primary" onClick={() => void saveCloseout()}>完成收尾</Button>
            </>
          }
        >
          <div className="closeout">
            <p className="modal-lead">只做必要的归位，不重新规划一整天。</p>
            {focusTasks.length === 0 ? (
              <div className="quiet-state">
                <ListTodo size={22} />
                <p>今天还没有聚焦事项。</p>
              </div>
            ) : (
              <div className="closeout-list">
                {focusTasks.map((task) => (
                  <div key={task.id} className={clsx("closeout-item", task.status === "done" && "done")}>
                    <div>
                      <strong>{task.title}</strong>
                      <small>{task.outcome}</small>
                    </div>
                    {task.status === "done" ? (
                      <span className="done-label"><Check size={15} /> 已完成</span>
                    ) : (
                      <div className="closeout-actions">
                        <button type="button" onClick={() => void markDone(task)}>
                          <CheckCircle2 size={16} /> 完成
                        </button>
                        <button type="button" onClick={() => void moveToTomorrow(task)}>
                          <RotateCcw size={16} /> 明天
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <label className="field">
              <span className="field-label">今天留下了什么判断？</span>
              <textarea
                className="textarea"
                rows={4}
                value={closeNote}
                onChange={(event) => setCloseNote(event.target.value)}
                placeholder="一句话即可，例如：客户异议背后其实是交付确定性。"
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}

function TaskPicker({
  tasks,
  selectedIds,
  onSelect,
  onClose,
  onCreate
}: {
  tasks: Task[];
  selectedIds: string[];
  onSelect: (task: Task) => void;
  onClose: () => void;
  onCreate: () => void;
}) {
  const [query, setQuery] = useState("");
  const filtered = tasks.filter((task) =>
    `${task.title} ${task.area} ${task.outcome}`.toLowerCase().includes(query.toLowerCase())
  );
  return (
    <Modal
      title="选择今日聚焦"
      eyebrow={`最多三件事 · 已选 ${selectedIds.length}`}
      onClose={onClose}
      size="medium"
      footer={
        <>
          <Button type="button" onClick={onClose}>取消</Button>
          <Button type="button" variant="primary" onClick={onCreate}><Plus size={16} /> 新建待办</Button>
        </>
      }
    >
      <div className="picker-layout">
        <input
          className="input search-input"
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="搜索任务、领域或预期结果"
        />
        {filtered.length === 0 ? (
          <EmptyState
            icon={<Inbox size={23} />}
            title="没有匹配的待办"
            description="可以换个关键词，或直接新建一件。"
          />
        ) : (
          <div className="task-picker-list">
            {filtered.map((task) => {
              const selected = selectedIds.includes(task.id);
              const incomplete = !task.outcome.trim() || !task.nextAction.trim();
              return (
                <button
                  key={task.id}
                  type="button"
                  className={clsx("task-picker-row", selected && "selected")}
                  disabled={selected}
                  onClick={() => onSelect(task)}
                >
                  <span className="picker-radio">{selected ? <Check size={16} /> : <Circle size={16} />}</span>
                  <span>
                    <strong>{task.title}</strong>
                    <small>
                      {task.area || "未分类"}
                      {incomplete ? " · 选入前需补预期结果和下一步" : task.outcome ? ` · ${task.outcome}` : ""}
                    </small>
                  </span>
                  <StatusPill status={task.status} compact />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
